import React from "react";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { axiosPrivate } from "@/api/axios";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { uploadFileToFirebase } from "@/lib/firebase/mediaUploading";
import { toast } from "sonner";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MediaFormField from "@/components/ui/mediaFormField";
import GalleryMediaFormField from "@/components/ui/galleryMediaFormField";
import useProductCategories from "@/hooks/useProductCategories";

const formSchema = z.object({
  name: z.string().min(1, "Product Name is required"),
  description: z.string().min(1, "Description is required"),
  category_id: z.coerce.number().min(1, "Category is required"),
  sku: z.string().optional(),
  company: z.string().optional(),
  product_group: z.string().optional(),
  tags: z.string().optional(),
  best_seller: z.string().optional(),
  quantity: z.coerce.number().optional(),
  length: z.coerce.number().optional(),
  width: z.coerce.number().optional(),
  height: z.coerce.number().optional(),
  net_weight: z.coerce.number().optional(),
  gross_weight: z.coerce.number().optional(),
  volume: z.coerce.number().optional(),
});

function AddProduct() {
  const queryClient = useQueryClient();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      category_id: 0,
      sku: "",
      company: "",
      product_group: "",
      tags: "",
      best_seller: "",
      quantity: 0,
      length: 0,
      width: 0,
      height: 0,
      net_weight: 0,
      gross_weight: 0,
      volume: 0,
    },
  });
  const [image, setImage] = React.useState<File | null>(null),
    [imageUrl, setImageUrl] = React.useState(""),
    [thumbnail, setThumbnail] = React.useState<File | null>(null),
  [thumbnailUrl, setThumbnailUrl] = React.useState(""),
  [galleryFiles, setGalleryFiles] = React.useState<File[]>([]),
  [galleryUrls, setGalleryUrls] = React.useState<string[]>([]);
  const navigate = useNavigate();

  // Use product categories from /categories/
  const { data: categories, isLoading, isError: categoriesError, refetch: refetchCategories } = useProductCategories();

  // Compute Firebase folder paths based on selected category name
  const safeSegment = React.useCallback((s: string) => {
    // Firebase Storage path segment cannot contain # [ ] * or ? and we avoid slashes
    return s
      .replace(/[#\[\]\*\?]/g, "-")
      .replace(/[\/]+/g, "-")
      .trim();
  }, []);

  // Normalize category name for reliable mapping (case-insensitive, '&' ~ 'and', drop punctuation)
  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '') // strip non-alphanumerics
      .trim();
  // Map normalized names to Firebase folder names
  const categoryFolderMap: Record<string, string> = {
    // Food & Beverage group
    [normalize('Food and Beverages')]: 'F&B',
    // Health & Dietary group (note: API uses "Suppliments" with 'i')
    [normalize('Health and Dietary Suppliments')]: 'H&D',
    // Personal & Care group
    [normalize('Personal Care and Cosmetics')]: 'P&C',
  // Others group
  [normalize('Others')]: 'Others',
  };

  const selectedCategory = React.useMemo(() => {
    const id = form.getValues("category_id");
    if (!categories || !id) return null;
    return categories.find((c) => Number(c.id) === Number(id)) || null;
  }, [categories, form.watch("category_id")]);

  const mappedCategoryName = selectedCategory?.name
    ? (categoryFolderMap[normalize(selectedCategory.name)] ?? 'Others')
    : null;
  
  const baseFolder = mappedCategoryName
    ? `DXN Products/${safeSegment(mappedCategoryName)}`
    : null;
  const imageRefPath = baseFolder ? `${baseFolder}/images` : "";
  const thumbRefPath = baseFolder ? `${baseFolder}/thumbnails` : "";
  const galleryRefPath = baseFolder ? `${baseFolder}/images` : ""; // or 'gallery' if you want a separate folder

  const uploadsDisabled = !baseFolder;

  const createProductMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      // Clean up the data - remove empty string and zero values for optional fields
      const cleanedData = {
        name: data.name,
        description: data.description,
        category_id: data.category_id,
        ...(data.sku && { sku: data.sku }),
        ...(data.company && { company: data.company }),
        ...(data.product_group && { product_group: data.product_group }),
        ...(data.tags && { tags: data.tags }),
        ...(data.best_seller && { best_seller: data.best_seller }),
        ...(data.quantity && data.quantity > 0 && { quantity: data.quantity }),
        ...(data.length && data.length > 0 && { length: data.length }),
        ...(data.width && data.width > 0 && { width: data.width }),
        ...(data.height && data.height > 0 && { height: data.height }),
        ...(data.net_weight && data.net_weight > 0 && { net_weight: data.net_weight }),
        ...(data.gross_weight && data.gross_weight > 0 && { gross_weight: data.gross_weight }),
        ...(data.volume && data.volume > 0 && { volume: data.volume }),
        ...(imageUrl && { image_url: imageUrl }),
        ...(thumbnailUrl && { thumbnail_url: thumbnailUrl }),
        ...(galleryUrls.length > 0 && { image_urls: galleryUrls }),
      };
      
      const res = await axiosPrivate.post("/products/", cleanedData);
      if (!res.data.success) {
        throw new Error("Failed to add product");
      }
      return res.data;
    },
    onSuccess: () => {
      toast.success("Product added successfully");
      form.reset();
      setImage(null);
      setImageUrl("");
      setThumbnail(null);
      setThumbnailUrl("");
  setGalleryFiles([]);
  setGalleryUrls([]);
      queryClient.invalidateQueries({ queryKey: ["products"] });
      navigate("/dashboard/products/manage-product");
    },
    onError: (error: Error & { response?: { data?: { message?: string; data?: Record<string, string | string[]> } } }) => {
      if (error?.response?.data) {
        // Display specific validation errors if available
        const validationErrors = error.response.data.data;
        if (validationErrors && typeof validationErrors === 'object') {
          const errorMessages = Object.entries(validationErrors).map(
            ([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`
          ).join('\n');
          toast.error(`Validation errors:\n${errorMessages}`);
        } else {
          toast.error(`Failed to add product: ${error.response.data.message || 'Unknown error'}`);
        }
      } else {
        toast.error(error.message || "Failed to add product");
      }
    },
  });

  async function handleSubmit(data: z.infer<typeof formSchema>) {
    createProductMutation.mutate(data);
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      <h1 className="text-2xl">Add Product</h1>
      <Form {...form}>
        <form className="space-y-4 sm:space-y-8" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Product Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Product Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter Description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>SKU</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter SKU" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Company Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Category</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value ? field.value.toString() : ""}
                      disabled={isLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={isLoading ? "Loading categories..." : "Select Category"} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoading && (
                          <div className="px-2 py-1 text-sm text-muted-foreground">Loading...</div>
                        )}
                        {!isLoading && categories && categories.length === 0 && (
                          <div className="px-2 py-1 text-sm text-muted-foreground">No categories found</div>
                        )}
                        {!isLoading && categories && categories.length > 0 && (
                          categories.map((category) => (
                            <SelectItem key={category.id} value={category.id.toString()}>
                              {category.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="product_group"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Product Group</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Product Group" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Tags (comma separated)" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="best_seller"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Best Seller</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter Best Seller" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Quantity" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Dimensions</h3>
            <div className="grid grid-cols-3 gap-5">
              <FormField
                control={form.control}
                name="length"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Length</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Length" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="width"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Width</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Width" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="height"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Height</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Height" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Weight & Volume</h3>
            <div className="grid grid-cols-3 gap-5">
              <FormField
                control={form.control}
                name="net_weight"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Net Weight (grams)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Net Weight" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="gross_weight"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Gross Weight (grams)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Gross Weight" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="volume"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Volume</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Volume" 
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Images</h3>
            <div>
                <label className="block text-sm font-medium mb-2">Product Thumbnail</label>
                <p className="text-xs text-muted-foreground mt-1">Image resolution 1024x1024 and size 2MB max</p>

                <MediaFormField
                  media={thumbnail}
                  setMedia={setThumbnail}
                  setMediaUrl={setThumbnailUrl}
                  refPath={thumbRefPath}
                  accept="image/*"
                  uploadDisabled={uploadsDisabled}
                />
                {uploadsDisabled && (
                  <p className="text-xs text-muted-foreground mt-1">Select a Category to enable uploads. Thumbnails will be saved under DXN Products/&lt;Category&gt;/thumbnails.</p>
                )}
              </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product Image</label>
                <p className="text-xs text-muted-foreground mt-1">Image resolution 1024x1024 and size 2MB max</p>

                <MediaFormField
                  media={image}
                  setMedia={setImage}
                  setMediaUrl={setImageUrl}
                  refPath={imageRefPath}
                  accept="image/*"
                  uploadDisabled={uploadsDisabled}
                />
                {uploadsDisabled && (
                  <p className="text-xs text-muted-foreground mt-1">Select a Category to enable uploads. Files will be saved under DXN Products/&lt;Category&gt;/images.</p>
                )}
              </div>
              {/* <div>
                <label className="block text-sm font-medium mb-2">Product Thumbnail</label>
                <MediaFormField
                  media={thumbnail}
                  setMedia={setThumbnail}
                  setMediaUrl={setThumbnailUrl}
                  refPath={thumbRefPath}
                  accept="image/*"
                  uploadDisabled={uploadsDisabled}
                />
                {uploadsDisabled && (
                  <p className="text-xs text-muted-foreground mt-1">Select a Category to enable uploads. Thumbnails will be saved under DXN Products/&lt;Category&gt;/thumbnails.</p>
                )}
              </div> */}
              {/* <div>
                <label className="block text-sm font-medium mb-2">Gallery Images</label>
                <GalleryMediaFormField
                  files={galleryFiles}
                  setFiles={setGalleryFiles}
                  urls={galleryUrls}
                  setUrls={setGalleryUrls}
                  refPath={galleryRefPath}
                  uploadDisabled={uploadsDisabled}
                  accept="image/*"
                />
                {uploadsDisabled && (
                  <p className="text-xs text-muted-foreground mt-1">Select a Category to enable uploads. Gallery images will be saved under DXN Products/&lt;Category&gt;/images.</p>
                )}
              </div> */}
            </div>
          </div>

          <Button type="submit" disabled={!form.formState.isValid}>
            Add Product
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default AddProduct;

// function ImageField({
//   image,
//   setImage,
//   setImageUrl,
// }: {
//   image: File | null;
//   setImage: React.Dispatch<React.SetStateAction<File | null>>;
//   setImageUrl: React.Dispatch<React.SetStateAction<string>>;
// }) {
//   const [isUploading, setIsUploading] = React.useState(false),
//     [isUploadSuccess, setIsUploadSuccess] = React.useState(false),
//     [isOpen, setIsOpen] = React.useState(false);

//   async function handleImageUpload() {
//     try {
//       if (!image) return;
//       setIsUploading(true);
//       const imageUrl = await uploadFileToFirebase(image);
//       setImageUrl(imageUrl);
//       setIsUploading(false);
//       setIsUploadSuccess(true);
//       toast.success("Image uploaded successfully");
//
//     } catch (error) {
//
//       toast.error("Failed to upload image");
//     } finally {
//       setIsUploading(false);
//     }
//   }

//   return (
//     <>
//       <Collapsible open={isOpen} onOpenChange={setIsOpen}>
//         <div className="flex items-center gap-1">
//           <CollapsibleTrigger asChild>
//             <Button
//               variant={"ghost"}
//               size={"icon"}
//               className={`size-8 hover:bg-green-300 ${image ? "" : "hidden"}`}
//             >
//               <ChevronsUpDown />
//             </Button>
//           </CollapsibleTrigger>
//           <Input
//             type="file"
//             accept=".png, .jpeg, .jpg"
//             onChange={(e) => {
//               const file = e.target.files?.[0];
//               setImage(file || null);
//               setIsOpen(true);
//             }}
//           />
//           <Button
//             type="button"
//             disabled={isUploading || image == null || isUploadSuccess}
//             className="ml-10"
//             onClick={handleImageUpload}
//           >
//             {isUploading
//               ? "Uploading Image"
//               : isUploadSuccess
//               ? "Uploaded Successfully"
//               : "Upload Image"}
//           </Button>
//         </div>
//         <CollapsibleContent>
//           <div className="mt-4">
//             <img
//               src={image ? URL.createObjectURL(image) : undefined}
//               className="w-full h-full object-contain rounded-md"
//             />
//           </div>
//         </CollapsibleContent>
//       </Collapsible>
//     </>
//   );
// }
